import Cookies from 'js-cookie';
import storageAvailable from './storageAvailable';

const localStorageEnabled = storageAvailable('localStorage');
const inMemoryStorage = new Map<string, string>();

const ClientStorage = {
    getItem(key: string): string | undefined {
        let value: string | undefined;

        if (this._shouldUseDomainCookie(key)) {
            value = Cookies.get(key);
        } else if (localStorageEnabled) {
            value = window.localStorage.getItem(key) ?? undefined;
        } else {
            value = inMemoryStorage.get(key);
        }

        return value;
    },

    setItem(key: string, value: unknown): void {
        const stringValue = String(value);

        if (this._shouldUseDomainCookie(key)) {
            if (Cookies.get(key) === stringValue) return;

            this.setClientDomainCookie(key, stringValue);
            if (this._getCookieDomain()) this._setDomainCookie(key, value);
        } else if (localStorageEnabled) {
            window.localStorage.setItem(key, stringValue);
        } else {
            inMemoryStorage.set(key, stringValue);
        }
    },

    removeItem(key: string): void {
        if (this._shouldUseDomainCookie(key)) {
            Cookies.remove(key, { path: '/', domain: this._getCookieDomain() });
        } else if (localStorageEnabled) {
            window.localStorage.removeItem(key);
        } else {
            inMemoryStorage.delete(key);
        }
    },

    keys(): string[] {
        if (localStorageEnabled) return Object.keys(window.localStorage);

        return Array.from(inMemoryStorage.keys());
    },

    removeItemsByPattern(regex: RegExp): void {
        this.keys().forEach(storageKey => {
            if (regex.test(storageKey)) this.removeItem(storageKey);
        });
    },

    setClientDomainCookie(key: string, value: unknown, overrides: Cookies.CookieAttributes = {}): void {
        Cookies.set(key, String(value), {
            path: '/',
            domain: this._getCookieDomain(),
            // Some browsers (notably Safari) have a seven-day max expiration policy for
            // cookies set on the client
            // See https://webkit.org/blog/8613/intelligent-tracking-prevention-2-1/
            // See https://www.cookiestatus.com/
            expires: 3650, // 365 days * 10 years
            ...overrides,
        });
    },

    removeDomainCookie(key: string): void {
        Cookies.remove(key, { path: '/', domain: this._getCookieDomain() });
    },

    getCookie(key: string): unknown {
        const cookieValue = Cookies.get(key);
        if (cookieValue == null) return cookieValue;

        try {
            return JSON.parse(cookieValue) as unknown;
        } catch {
            return cookieValue;
        }
    },

    async _setDomainCookie(key: string, value: unknown, retries: number = 2): Promise<void> {
        try {
            const response = await window.fetch(`${window.ENDPOINT_ROOT}/api/status/set_domain_cookie.json`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(window.CF_ACCESS_CLIENT_ID && window.CF_ACCESS_CLIENT_SECRET
                        ? {
                              'CF-Access-Client-Id': window.CF_ACCESS_CLIENT_ID,
                              'CF-Access-Client-Secret': window.CF_ACCESS_CLIENT_SECRET,
                          }
                        : {}),
                },
                body: JSON.stringify({ name: key, value }),
            });

            if (!response.ok && retries > 0) {
                await this._setDomainCookie(key, value, retries - 1);
            }
        } catch {
            // Silently ignore network errors — the client-side cookie is already set
        }
    },

    _getCookieDomain(): string | undefined {
        // Not applicable in localhost or a custom aws staging server
        // See also PedagoDomains#cookie_domain
        let cookieDomain: string | undefined;

        if (window.location.hostname.includes('quantic.edu')) {
            cookieDomain = 'quantic.edu';
        } else if (window.location.hostname.includes('smart.ly')) {
            cookieDomain = 'smart.ly';
        } else if (window.location.hostname.includes('staging-quantic.com')) {
            cookieDomain = 'staging-quantic.com';
        } else if (window.location.hostname.includes('staging-smartly.com')) {
            cookieDomain = 'staging-smartly.com';
        } else if (window.location.hostname.includes('ngrok')) {
            cookieDomain = `${window.preloadedConfig?.ngrok_subdomain}.ngrok.io`;
        }

        return cookieDomain;
    },

    _shouldUseDomainCookie(key: string): boolean {
        // FIXME: Using this list to detect when a domain cookie is required is disjointed and confusing
        // to the caller of setItem. We should instead make a separate setDomainCookieItem method.
        return (
            ['auid', 'continueApplicationInMarketing', 'oauth_redirect_target', 'prefilledEmail'].includes(key) &&
            !(window.CORDOVA || window.RUNNING_IN_TEST_MODE)
        );
    },
};

export default ClientStorage;
